import { createWithEqualityFn } from 'zustand/traditional';

import { FavoriteCollegeSurvey, SurveyResult } from '@common-types';

import { SurveyQuestionsData } from '@interfaces/favoriteSurvey';

import { SURVEYS_ID } from '@constants/surveys';

import { postSchoolFavoriteSurvey } from '@api/surveys';

import useUserPreferencesStore from '@store/UserPreferencesStore';

interface SchoolSurveyData {
  [key: string]: SurveyQuestionsData;
}

interface SchoolSurveyDataStore {
  favoriteSchoolSurveys: SchoolSurveyData;
  isLoading: boolean;
  getSchoolFavoriteSurvey: (schoolId: number) => Promise<SurveyQuestionsData>;
  setSchoolFavoriteSurvey: (data: FavoriteCollegeSurvey) => void;
}

const initialState = {
  favoriteSchoolSurveys: {} as SchoolSurveyData,
  isLoading: false,
} as SchoolSurveyDataStore;

export const useSchoolSurveyStore = createWithEqualityFn<SchoolSurveyDataStore>()(
  (set, get) => ({
    ...initialState,

    getSchoolFavoriteSurvey: async (schoolId: number) => {
      set({ isLoading: true });

      const favoriteSchools = useUserPreferencesStore.getState().schoolFavorites;

      const surveyAnswers =
        favoriteSchools
          .find((school) => school.id === schoolId)
          ?.surveys?.find((survey) => survey.surveyId === SURVEYS_ID.FAVORITE_SCHOOLS)?.questions || [];

      const preparedData: SurveyQuestionsData = surveyAnswers.reduce(
        (previousValue: SurveyQuestionsData, currentValue: SurveyResult) => {
          // eslint-disable-next-line
          previousValue[currentValue.questionId] = currentValue.answer;

          return previousValue;
        },
        {},
      );

      const favoriteSchoolSurveys = { ...get().favoriteSchoolSurveys };

      favoriteSchoolSurveys[schoolId] = preparedData;

      set({ favoriteSchoolSurveys, isLoading: false });

      return get().favoriteSchoolSurveys[schoolId];
    },

    setSchoolFavoriteSurvey: async (data: FavoriteCollegeSurvey) => {
      const userPreferences = await postSchoolFavoriteSurvey(data);

      const preparedData: SurveyQuestionsData = data.questions.reduce(
        (previousValue: SurveyQuestionsData, currentValue: SurveyResult) => {
          // eslint-disable-next-line
          previousValue[currentValue.questionId] = currentValue.answer;

          return previousValue;
        },
        {},
      );

      const favoriteSchoolSurveys = { ...get().favoriteSchoolSurveys };

      favoriteSchoolSurveys[data.collegeId as number] = preparedData;

      set({ favoriteSchoolSurveys });

      useUserPreferencesStore.getState().setUserPreferences(userPreferences);
    },
  }),
  Object.is,
);
